// regimes.ts — regime predicates: the single source of truth for which pricing regime a line
// belongs to. Adding a regime means extending the relevant predicate here, with no caller change;
// a structural test guards against literal `type ===` comparisons outside this file.

import type { QuoteLineType } from "./core";

/** Minimal view the predicates need: only `type` discriminates, so both `QuoteLine` and `PricedLine` fit. */
type WithType = { type: QuoteLineType };

/**
 * Quantity-tier regime cumulated per family: STAND and TRANSFER share the cumul machinery,
 * while their differing thresholds and PA behaviour are spelled out in each regime.
 *
 * @param l A quote line or priced line (only `type` is read).
 * @returns True when the line belongs to this regime.
 */
export const isTierRegime = (l: WithType): boolean => l.type === "STAND" || l.type === "TRANSFER";

/**
 * TRANSFER — TRS family. Subset of the tier regime with its own thresholds (6/11) and a rigid PA.
 *
 * @param l A quote line or priced line (only `type` is read).
 * @returns True when the line belongs to this regime.
 */
export const isTransferRegime = (l: WithType): boolean => l.type === "TRANSFER";

/**
 * Unit regime — unit price × quantity, no tier or cumul: SUR_UNIT and MENUIS_FAB. Both require a negotiated PA.
 *
 * @param l A quote line or priced line (only `type` is read).
 * @returns True when the line belongs to this regime.
 */
export const isUnitRegime = (l: WithType): boolean => l.type === "SUR_UNIT" || l.type === "MENUIS_FAB";

/**
 * MENUIS_FAB — custom joinery / fabrication. Subset of the unit regime with its own workshop family.
 *
 * @param l A quote line or priced line (only `type` is read).
 * @returns True when the line belongs to this regime.
 */
export const isJoineryFabRegime = (l: WithType): boolean => l.type === "MENUIS_FAB";

/**
 * PRINT_GF — standard large-format print (m² × material, tier per category).
 *
 * @param l A quote line or priced line (only `type` is read).
 * @returns True when the line belongs to this regime.
 */
export const isLargeFormatPrintRegime = (l: WithType): boolean => l.type === "PRINT_GF";

/**
 * SUR_PRINT — custom print (total entered manually).
 *
 * @param l A quote line or priced line (only `type` is read).
 * @returns True when the line belongs to this regime.
 */
export const isCustomPrintRegime = (l: WithType): boolean => l.type === "SUR_PRINT";

/**
 * ACC_ML — accessory priced per linear metre.
 *
 * @param l A quote line or priced line (only `type` is read).
 * @returns True when the line belongs to this regime.
 */
export const isAccessoryMlRegime = (l: WithType): boolean => l.type === "ACC_ML";

/**
 * SVC — service (generated hourly cutting line).
 *
 * @param l A quote line or priced line (only `type` is read).
 * @returns True when the line belongs to this regime.
 */
export const isServiceRegime = (l: WithType): boolean => l.type === "SVC";

// The type of a line is frozen at creation: no conversion (a standard print and a custom print are not interchangeable).
const TYPE_CONVERSIONS: Partial<Record<QuoteLineType, QuoteLineType[]>> = {};

/**
 * Types a line of type `t` may switch to after creation. A single-entry result means frozen.
 *
 * @param t The current line type.
 * @returns The allowed target types, including `t` itself.
 */
export const allowedTypeConversions = (t: QuoteLineType): QuoteLineType[] => TYPE_CONVERSIONS[t] ?? [t];
